import { Link } from "react-router-dom";
import { ChevronLeft, MessageSquareText } from "lucide-react";

// Plain, human-readable Privacy Policy. Play Console + AdSense require an
// externally-linkable URL — this page is served at /privacy on notelosh.com.
export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6]">
      <header className="sticky top-0 z-10 bg-[#0A0A0A]/90 backdrop-blur border-b border-[#27272A]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors">
            <ChevronLeft className="h-4 w-4" /> Back
          </Link>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center overflow-hidden">
              <img src="/icons/logo-nl.png" alt="" className="h-5 w-5 object-contain" />
            </div>
            <span className="font-heading text-base font-bold tracking-tight">NoteLosh</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-[#6B7280] mb-10">Last updated: 30 October 2026</p>

        <div className="space-y-8 text-[#D1D5DB] leading-relaxed">
          <section>
            <p>NoteLosh ("we", "us", "our") operates the NoteLosh web app and Android app (the "Service"). This policy explains what personal data we collect, why, and your rights over it.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">1. Data we collect</h2>
            <h3 className="font-semibold text-[#F3F4F6] mt-4 mb-2">Account data</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="text-[#F3F4F6]">Email address</span> — required to create an account and sign in.</li>
              <li><span className="text-[#F3F4F6]">Name & profile picture URL</span> — optional, auto-filled from Google OAuth if you sign in with Google.</li>
              <li><span className="text-[#F3F4F6]">Password hash</span> — bcrypt hash for email/password accounts. We never store the plain password.</li>
            </ul>
            <h3 className="font-semibold text-[#F3F4F6] mt-4 mb-2">Content you create</h3>
            <p>Notes, links, file uploads, reminders, chat threads — stored on our servers so we can show them back to you across devices. Private by default, visible only to you.</p>
            <h3 className="font-semibold text-[#F3F4F6] mt-4 mb-2">Payment data</h3>
            <p>Card numbers, expiry, CVC are collected <span className="text-[#F3F4F6]">directly by Stripe</span>. We never see or store them. We keep the transaction session ID and order details (product, amount, timestamp) for tax and dispute purposes.</p>
            <h3 className="font-semibold text-[#F3F4F6] mt-4 mb-2">Session & usage data</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>JWT / session tokens in cookies + local storage on your device.</li>
              <li>Basic access logs (IP, user agent, timestamp) retained 30 days for security & abuse prevention.</li>
            </ul>
            <p className="mt-4">We do <span className="text-[#F3F4F6]">not</span> collect: location, contacts, microphone audio, camera video, or any data you did not explicitly enter into the app.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">2. How we use your data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide the Service — showing your notes, running AI search, delivering reminders, processing marketplace purchases.</li>
              <li>Send transactional emails — password resets, purchase receipts.</li>
              <li>Detect abuse and keep the platform safe.</li>
            </ul>
            <p className="mt-4">We do <span className="text-[#F3F4F6]">not</span> sell your data. We do <span className="text-[#F3F4F6]">not</span> use it for advertising outside the Service.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">3. Third-party processors</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="text-[#F3F4F6]">Stripe</span> — website payments, wallet top-ups (made on notelosh.com only) and seller payouts. See stripe.com/privacy.</li>
              <li><span className="text-[#F3F4F6]">Google Play Billing</span> — note purchases made inside the Android app. See policies.google.com/privacy.</li>
              <li><span className="text-[#F3F4F6]">OpenAI</span> — powers AI semantic search. We send your search query + snippets of your notes at query time. See openai.com/policies.</li>
              <li><span className="text-[#F3F4F6]">Emergent</span> — cloud hosting & auth. See emergent.host/privacy.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">4. Your rights</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access, export, or delete your account & data at any time from Settings.</li>
              <li>Contact us to exercise GDPR/CCPA rights (see below).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">5. Retention</h2>
            <p>Account data is kept while your account exists. On deletion, personal data is removed within 30 days; anonymised usage aggregates may be retained for analytics.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">6. Children</h2>
            <p>NoteLosh is not directed at children under 13. Do not use the Service if you are under 13.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">7. Changes</h2>
            <p>Material changes will be announced in-app or via email at least 14 days before taking effect.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">8. Contact</h2>
            <p>Email <a href="mailto:support@notelosh.com" className="text-[#3B82F6] hover:underline">support@notelosh.com</a>.</p>
          </section>
        </div>

        <div className="mt-14 pt-8 border-t border-[#27272A] flex items-center justify-between text-sm text-[#6B7280]">
          <Link to="/terms" className="hover:text-[#F3F4F6] transition-colors">Terms of Service →</Link>
          <Link to="/" className="hover:text-[#F3F4F6] transition-colors flex items-center gap-1.5">
            <MessageSquareText className="h-3.5 w-3.5" /> notelosh.com
          </Link>
        </div>
      </main>
    </div>
  );
}
